/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetNewRemoteSessionClientCallbackUrlRequest,
  GetNewRemoteSessionClientCallbackUrlSecurity,
} from "../models/operations/getnewremotesessionclientcallbackurl.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildNewRemoteSessionClientCallbackUrlQuery,
  NewRemoteSessionClientCallbackUrlQueryData,
  prefetchNewRemoteSessionClientCallbackUrl,
  queryKeyNewRemoteSessionClientCallbackUrl,
} from "./newRemoteSessionClientCallbackUrl.core.js";
export {
  buildNewRemoteSessionClientCallbackUrlQuery,
  type NewRemoteSessionClientCallbackUrlQueryData,
  prefetchNewRemoteSessionClientCallbackUrl,
  queryKeyNewRemoteSessionClientCallbackUrl,
};

export type NewRemoteSessionClientCallbackUrlQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getNewClientCallbackUrl remoteSessionClients
 *
 * @remarks
 * Get the redirect URI a remote_session_client created now in the caller's project registers with its upstream provider.
 */
export function useNewRemoteSessionClientCallbackUrl(
  request?: GetNewRemoteSessionClientCallbackUrlRequest | undefined,
  security?: GetNewRemoteSessionClientCallbackUrlSecurity | undefined,
  options?: QueryHookOptions<
    NewRemoteSessionClientCallbackUrlQueryData,
    NewRemoteSessionClientCallbackUrlQueryError
  >,
): UseQueryResult<
  NewRemoteSessionClientCallbackUrlQueryData,
  NewRemoteSessionClientCallbackUrlQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildNewRemoteSessionClientCallbackUrlQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getNewClientCallbackUrl remoteSessionClients
 *
 * @remarks
 * Get the redirect URI a remote_session_client created now in the caller's project registers with its upstream provider.
 */
export function useNewRemoteSessionClientCallbackUrlSuspense(
  request?: GetNewRemoteSessionClientCallbackUrlRequest | undefined,
  security?: GetNewRemoteSessionClientCallbackUrlSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    NewRemoteSessionClientCallbackUrlQueryData,
    NewRemoteSessionClientCallbackUrlQueryError
  >,
): UseSuspenseQueryResult<
  NewRemoteSessionClientCallbackUrlQueryData,
  NewRemoteSessionClientCallbackUrlQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildNewRemoteSessionClientCallbackUrlQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setNewRemoteSessionClientCallbackUrlData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: NewRemoteSessionClientCallbackUrlQueryData,
): NewRemoteSessionClientCallbackUrlQueryData | undefined {
  const key = queryKeyNewRemoteSessionClientCallbackUrl(...queryKeyBase);

  return client.setQueryData<NewRemoteSessionClientCallbackUrlQueryData>(
    key,
    data,
  );
}

export function invalidateNewRemoteSessionClientCallbackUrl(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "remoteSessionClients",
      "getNewClientCallbackUrl",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllNewRemoteSessionClientCallbackUrl(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "remoteSessionClients",
      "getNewClientCallbackUrl",
    ],
  });
}
